import React from 'react';
import { useFormikContext } from 'formik';

import { useStyles } from '~/assets/styles';
import AutocompleteFormik from '~/components/AutocompleteFormik';
import CheckboxFormik from '~/components/CheckboxFormik';
import Grid from '~/components/core/Atomic/Grid/Grid';
import TextFieldFormik from '~/components/TextFieldFormik';
import type { OrganizationModel } from '~/components/types/organization-types';
import useDataFetcher from '~/components/useDataFetcher';
import DAMAGE_ASSESSMENT_TYPES from '~/server_shared/generated-types/DAMAGE_ASSESSMENT_TYPES';
import type { DATypeConfigInfo } from '~/server_shared/openapi/data-contracts';
import { CONFIGURATION_FEATURES_NAMES } from '~/Types';
import { isFeatureEnabled } from '~/Utils';

const DAMAGE_ASSESSMENT_TYPE_KEYS = Object.fromEntries(Object.keys(DAMAGE_ASSESSMENT_TYPES).map((key) => [key, key]));

interface DamageAssessmentTypeDropdownProps {
  organization: OrganizationModel;
  coverage?: Record<string, unknown> | null;
  shouldDisableFieldFunction: (arg?: unknown) => boolean;
}

const DamageAssessmentTypeDropdown: React.FC<DamageAssessmentTypeDropdownProps> = ({
  organization,
  coverage,
  shouldDisableFieldFunction,
}) => {
  const { classes } = useStyles();
  const { values, setValues } = useFormikContext<Record<string, unknown>>();
  const isDamageAssessmentTypeConfigEnabled = isFeatureEnabled(
    organization,
    CONFIGURATION_FEATURES_NAMES.DAMAGE_ASSESSMENT_TYPE_CONFIG
  );
  const { data: damageAssessmentTypes } = useDataFetcher(
    isDamageAssessmentTypeConfigEnabled
      ? `/sysconfig/api/v1/organizations/${organization.id}/damage_assessment_type_configs`
      : null
  );

  const systemTypeKeys = Object.keys(DAMAGE_ASSESSMENT_TYPES);
  const userConfigKeys = ((damageAssessmentTypes as DATypeConfigInfo[]) || [])
    .filter((c) => !c.is_disabled || c.key === values.damage_assessment_type)
    .map((c) => c.key);
  const allOptions = Array.from(new Set([...systemTypeKeys, ...userConfigKeys]));

  const userConfigByKey = Object.fromEntries(
    ((damageAssessmentTypes as DATypeConfigInfo[]) || []).map((c) => [c.key, c])
  );
  const getLabel = (option: string) => {
    if ((DAMAGE_ASSESSMENT_TYPES as Record<string, string>)[option])
      return (DAMAGE_ASSESSMENT_TYPES as Record<string, string>)[option];
    if (userConfigByKey[option]) return userConfigByKey[option].display_name;
    return option;
  };

  return (
    <Grid container item xs={6}>
      <Grid item xs={12}>
        <AutocompleteFormik
          id="damage_assessment_type"
          label="Damage Assessment Type"
          options={allOptions}
          getOptionLabel={getLabel}
          onChange={(_: unknown, newVal: string | null) => {
            setValues({
              ...values,
              damage_assessment_type: newVal ?? '',
              ...(newVal === DAMAGE_ASSESSMENT_TYPE_KEYS.income_protection && {
                should_include_dates_of_service: true,
              }),
            });
          }}
          sortAlphabetic
          className={classes.formTextField}
          disabled={shouldDisableFieldFunction(coverage)}
        />
      </Grid>
      {values.damage_assessment_type === 'generic_invoice' ? (
        <>
          <Grid item xs={12}>
            <CheckboxFormik
              id="include_depreciation"
              label="Include Depreciation"
              disabled={shouldDisableFieldFunction(coverage)}
            />
          </Grid>
          {(values as Record<string, boolean>)?.include_depreciation ? (
            <Grid item xs={12}>
              <TextFieldFormik
                id="depreciation_calculation_api_url"
                label="Depreciation Calculation API URL"
                className={classes.formTextField}
                fullWidth
                disabled={shouldDisableFieldFunction(coverage)}
              />
            </Grid>
          ) : null}
        </>
      ) : null}
    </Grid>
  );
};

export default DamageAssessmentTypeDropdown;
